import { lockScroll, unlockScroll } from '@theme/utilities';

const STORAGE_KEY = 'age-verification-accepted';
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Age gate: a modal `<dialog>` shown until the visitor confirms their age.
 * The answer is stored in localStorage for `data-remember-days` days,
 * or in sessionStorage when that value is 0.
 */
class AgeVerification extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.dialog?.addEventListener('cancel', this.#onCancel);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onSectionSelect);
      document.addEventListener('shopify:section:load', this.#onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#onSectionDeselect);
      return;
    }

    if (!this.#hasAccepted()) this.open();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.dialog?.removeEventListener('cancel', this.#onCancel);
    document.removeEventListener('shopify:section:select', this.#onSectionSelect);
    document.removeEventListener('shopify:section:load', this.#onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#onSectionDeselect);
    unlockScroll(this);
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    return this.querySelector('dialog');
  }

  /** @returns {number} */
  get rememberDays() {
    const days = Number.parseInt(this.dataset.rememberDays ?? '', 10);
    return Number.isFinite(days) && days > 0 ? days : 0;
  }

  open() {
    const { dialog } = this;
    if (!dialog || dialog.open) return;

    this.#showStep('ask');
    dialog.showModal();
    lockScroll(this);
  }

  close() {
    const { dialog } = this;
    if (!dialog?.open) return;

    dialog.close();
    unlockScroll(this);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const control = event.target instanceof Element ? event.target.closest('[data-age-action]') : null;
    if (!(control instanceof HTMLElement)) return;

    switch (control.dataset.ageAction) {
      case 'accept':
        this.#remember();
        this.close();
        break;
      case 'decline':
        this.#decline();
        break;
      case 'back':
        this.#showStep('ask');
        break;
    }
  };

  /**
   * The gate must be answered, so Escape does not dismiss it.
   * @param {Event} event
   */
  #onCancel = (event) => {
    event.preventDefault();
  };

  #decline() {
    const exitUrl = this.dataset.exitUrl?.trim();
    if (exitUrl && !window.Shopify?.designMode) {
      window.location.assign(exitUrl);
      return;
    }
    this.#showStep('declined');
  }

  /**
   * @param {'ask' | 'declined'} step
   */
  #showStep(step) {
    this.querySelectorAll('[data-age-step]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      element.hidden = element.dataset.ageStep !== step;
    });

    const focusTarget = this.querySelector(
      step === 'ask' ? '[data-age-action="accept"]' : '[data-age-action="back"]'
    );
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
  }

  /** @returns {boolean} */
  #hasAccepted() {
    try {
      if (window.sessionStorage.getItem(STORAGE_KEY) === 'true') return true;

      const expiresAt = Number(window.localStorage.getItem(STORAGE_KEY));
      if (Number.isFinite(expiresAt) && expiresAt > Date.now()) return true;

      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage blocked (private mode, disabled cookies): ask on every page.
    }
    return false;
  }

  #remember() {
    if (window.Shopify?.designMode) return;

    try {
      const days = this.rememberDays;
      if (days > 0) {
        window.localStorage.setItem(STORAGE_KEY, String(Date.now() + days * DAY_MS));
      } else {
        window.sessionStorage.setItem(STORAGE_KEY, 'true');
      }
    } catch {
      // Storage blocked: the visitor is asked again on the next page.
    }
  }

  /**
   * @param {Event} event
   */
  #onSectionSelect = (event) => {
    if (this.#isOwnSection(event)) this.open();
  };

  /**
   * @param {Event} event
   */
  #onSectionDeselect = (event) => {
    if (this.#isOwnSection(event)) this.close();
  };

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #isOwnSection(event) {
    const detail = event instanceof CustomEvent ? event.detail : null;
    return detail?.sectionId === this.dataset.sectionId;
  }
}

if (!customElements.get('age-verification')) {
  customElements.define('age-verification', AgeVerification);
}
